<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'

const props = defineProps<{
  open: boolean
  title: string
  description?: string
  desktopClass?: string
  mobileClass?: string
  headerClass?: string
  nested?: boolean
}>()

const emit = defineEmits<{ 'update:open': [open: boolean] }>()
const isDesktop = useMediaQuery('(min-width: 768px)')
const mounted = ref(false)
const presentation = ref<'dialog' | 'drawer'>('drawer')

function syncPresentation() {
  presentation.value = isDesktop.value ? 'dialog' : 'drawer'
}

onMounted(() => {
  syncPresentation()
  mounted.value = true
})
watch(() => props.open, open => {
  if (open) syncPresentation()
})
watch(isDesktop, () => {
  if (!props.open) syncPresentation()
})
</script>

<template>
  <Dialog v-if="mounted && presentation === 'dialog'" :open="props.open" @update:open="emit('update:open', $event)">
    <DialogContent :class="['flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0', desktopClass]">
      <DialogHeader :class="['shrink-0 px-5 pt-5 text-left', headerClass]">
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription :class="description ? undefined : 'sr-only'">{{ description || title }}</DialogDescription>
      </DialogHeader>
      <slot />
    </DialogContent>
  </Dialog>
  <Drawer v-else-if="mounted" :open="props.open" :nested="nested" :should-scale-background="false" @update:open="emit('update:open', $event)">
    <DrawerContent :overlay-class="nested ? 'z-[70]' : undefined" :class="['max-h-[90dvh] gap-0 overflow-hidden rounded-t-3xl border-0 p-0', nested && 'z-[70]', mobileClass]">
      <DrawerHeader :class="['shrink-0 px-5 pt-4 text-left', headerClass]">
        <DrawerTitle>{{ title }}</DrawerTitle>
        <DrawerDescription :class="description ? undefined : 'sr-only'">{{ description || title }}</DrawerDescription>
      </DrawerHeader>
      <slot />
    </DrawerContent>
  </Drawer>
</template>
